import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import SecretDesParents from './SecretDesParents';

/**
 * « Le scénario » — la soirée en 4 photos réelles : le jeu qui sort au
 * dessert, le bruit pour répondre, le crescendo, la pièce qu'on gratte.
 * Suivi du « secret des parents » (SecretDesParents), partagé avec la fiche
 * produit et la page des règles.
 *
 * L'accueil ne montrait que des illustrations : le visiteur ne voyait jamais
 * ce qu'il allait recevoir avant d'arriver sur la boutique. Ces quatre images
 * sont les photos du produit tel qu'il est expédié.
 *
 * Style volontairement sobre (fond clair, ombre douce, pas de bordure épaisse)
 * pour laisser les photos porter la section.
 */

export default function HowItWorks() {
  const { t } = useLang();

  // Le tableau vivait au niveau du module ; il descend ici car ses textes
  // passent par t (un hook).
  const ETAPES = [
    {
      n: 1,
      titre: t('how.etape1.titre'),
      texte: t('how.etape1.texte'),
      photo: '/photos/contenu.webp',
      largeur: 1200,
      alt: t('how.etape1.alt'),
    },
    {
      n: 2,
      titre: t('how.etape2.titre'),
      texte: t('how.etape2.texte'),
      photo: '/photos/pochons.webp',
      largeur: 1200,
      alt: t('how.etape2.alt'),
    },
    {
      n: 3,
      titre: t('how.etape3.titre'),
      texte: t('how.etape3.texte'),
      photo: '/photos/puzzle-table.webp',
      largeur: 1200,
      alt: t('how.etape3.alt'),
    },
    {
      n: 4,
      titre: t('how.etape4.titre'),
      texte: t('how.etape4.texte'),
      photo: '/photos/grattage.webp',
      largeur: 768,
      alt: t('how.etape4.alt'),
    },
  ];

  return (
    <section className="bg-gradient-to-b from-white to-[#F7F4EE] px-5 py-16 md:py-20">
      <div className="mx-auto max-w-[1200px]">
        <div className="mx-auto mb-12 max-w-[620px] text-center">
          <p className="m-0 mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#4AA294]">
            {t('how.badge')}
          </p>
          <h2 className="m-0 mb-4 text-3xl font-black uppercase tracking-tight text-[#33403B] md:text-4xl">
            {t('how.titre')}
          </h2>
          <p className="m-0 text-[15px] leading-relaxed text-[#33403B]/75">
            {t('how.description')}
          </p>
        </div>

        <ol className="m-0 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-4">
          {ETAPES.map((e) => (
            <li
              key={e.n}
              className="group overflow-hidden rounded-2xl bg-white shadow-[0_6px_24px_rgba(47,126,114,0.10)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(47,126,114,0.18)]"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F2EA]">
                <img
                  src={e.photo}
                  srcSet={`${e.photo.replace('.webp', '-600.webp')} 600w, ${e.photo} ${e.largeur}w`}
                  sizes="(max-width: 640px) 92vw, (max-width: 1024px) 46vw, 280px"
                  alt={e.alt}
                  loading="lazy"
                  decoding="async"
                  width="1200"
                  height="900"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[#17453E]/90 text-base font-black text-[#EDB82F] backdrop-blur-sm">
                  {e.n}
                </span>
              </div>
              <div className="p-5">
                <h3 className="m-0 mb-2 text-base font-black text-[#33403B]">{e.titre}</h3>
                <p className="m-0 text-[13px] leading-relaxed text-[#33403B]/75">{e.texte}</p>
              </div>
            </li>
          ))}
        </ol>

        <SecretDesParents className="mx-auto mt-10 max-w-[760px]" />

        <p className="mt-10 text-center text-sm text-[#33403B]/70">
          <Link
            to="/regles-de-jeu"
            data-cta="home:vers-regles"
            className="font-bold text-[#2F7E72] underline decoration-[#4AA294]/40 underline-offset-4 transition-colors hover:text-[#17453E] hover:decoration-[#EDB82F]"
          >
            {t('how.lienRegles')}
          </Link>{' '}
          {t('how.lienReglesSuite')}
        </p>
      </div>
    </section>
  );
}
